/**
 * Shared tokens and auth-error translation for the light split-card login.
 *
 * Black and white on white, Inter — the same language as the landing page
 * (`marketing/hero/hero-tokens.ts`). Portal hosts are the exception: those
 * screens run on the organisation's own brand color, passed in at runtime.
 *
 * The auth-error copy lives in `<locale>/public.json` under `loginShared`;
 * `translateAuthError()` takes a translator scoped to that section. The map
 * keys stay untouched — they are Better-Auth codes matched against, not copy.
 */

import type { Translate } from "@/lib/i18n/translate";

/** Primary accent on our own auth screens: ink, not verdigris. */
export const ACCENT = "#0B0F19";

/** Shadow tint under the submit button. */
export const ACCENT_SHADOW = "rgba(11,15,25,0.35)";

/** Focus ring shown around inputs while focused. */
export const FOCUS_SHADOW = "0 0 0 3px rgba(11,15,25,0.12)";

/** Surface tokens. */
export const PAGE_BG = "#F4F4F5";
export const CARD_BG = "#FFFFFF";
export const INK = "#0B0F19";
export const DARK_PANEL_BG = "#0B0F19";
export const INPUT_BG = "#FFFFFF";
export const INPUT_BORDER = "#E7E9ED";

/** Better-Auth code/message → message key. Keys are matched, never displayed. */
function authErrorKeyMap(): Record<string, string> {
  return {
    "Invalid email or password": "ungueltigeEMailPasswort",
    "Invalid email": "ungueltigeEMailAdresse",
    "Invalid password": "ungueltigesPasswort",
    "User not found": "benutzerNichtGefunden",
    "User already exists": "benutzerDieserEMail",
    "User already exists. Use another email.": "benutzerDieserEMail",
    "Email is not verified": "eMailAdresseNicht",
    "Too many requests": "vieleAnfragenBitteVersuche",
    "Internal server error": "internerFehlerAufgetreten",
    "Invalid origin": "anmeldungFehlgeschlagenBitteVersuche",
    INVALID_ORIGIN: "anmeldungFehlgeschlagenBitteVersuche",
    "Invalid callbackURL": "anmeldungFehlgeschlagenBitteVersuche",
    INVALID_CALLBACK_URL: "anmeldungFehlgeschlagenBitteVersuche",
    INVALID_EMAIL_OR_PASSWORD: "ungueltigeEMailPasswort",
  };
}

/**
 * Translate a Better-Auth error code/message to a user-facing string.
 *
 * `useTranslations("public.loginShared")`.
 */
export function translateAuthError(t: Translate, message: string): string {
  const key: string | undefined = authErrorKeyMap()[message];
  return t(key ?? "anmeldungFehlgeschlagen");
}
